import React, { useState, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { 
  FaDraftingCompass, FaTools, FaTruckPickup, FaClipboardCheck, FaSearchDollar, 
  FaPlayCircle, FaChevronRight, FaCheck, FaPaintRoller, FaHammer, FaTree, 
  FaSwimmingPool, FaHome, FaLayerGroup, FaPlay, FaClock, FaArrowRight
} from 'react-icons/fa';



// Shared Components
import Header from './components/Header';
import Footer from './components/Footer';
import SearchFormComponent from "../../pages/Dashboards/SearchFormComponent"; 
import SocialProofEngine from '../../components/SocialProof/SocialProofEngine';

import ScamDetectorWidget from './components/ScamDetectorWidget';

import AppDownloadSection from './components/AppDownloadSection'; 
import TestimonialsSection from './components/TestimonialsSection'; 
import FinalCtaSection from './components/FinalCtaSection'; 

import './ResourcesHubPageNew.css';

// --- DATA: PROFESSIONALS ---
const professionals = [
  { name: "Architect", phase: "Planning & Design", color: "#6366f1", icon: <FaDraftingCompass />, desc: "Designs the master plan and creates detailed blueprints." },
  { name: "Quantity Surveyor", phase: "Pre-Construction", color: "#10b981", icon: <FaSearchDollar />, desc: "Calculates material costs to keep budgets real." },
  { name: "Concreter", phase: "Construction (Stage 1)", color: "#f59e0b", icon: <FaTruckPickup />, desc: "Forms, pours, and finishes the concrete slab foundation." },
  { name: "Carpenter", phase: "Construction (Stage 2)", color: "#8b5cf6", icon: <FaHammer />, desc: "Builds the structural frame, roof trusses, and decking." },
  { name: "Electrician", phase: "Fit-out", color: "#ef4444", icon: <FaTools />, desc: "Handles high-risk wiring, safety switches, and power." },
  { name: "Certifier", phase: "Handover", color: "#3b82f6", icon: <FaClipboardCheck />, desc: "Inspects work at critical stages to ensure compliance." }
];

// --- DATA: VIDEO SCRIPTS ---
const videoScripts = [
  {
    id: 1,
    title: "The 'Variation' Trap",
    duration: "3 MIN",
    desc: "Why costs change mid-project and how to document them.",
    content: (
      <>
        <p><strong>Scene:</strong> Homeowner holds a contract looking confused. Builder holds a blueprint.</p>
        <p><strong>Narrator:</strong> "This is the most common way budgets blow out: The Variation."</p>
        <p><strong>Visual:</strong> Builder points to a wall. "We hit rock." Price tag pops up +$5,000.</p>
        <p><strong>Narrator:</strong> "A variation is ANY change to the original scope. If it's not written down, it's not happening."</p>
        <div className="rh-expert-highlight"><strong>Expert Tip:</strong> Never authorize extra work verbally. Always demand a 'Variation Order' signed by both parties BEFORE the work starts.</div>
      </>
    )
  },
  {
    id: 2,
    title: "The Vetting Gauntlet",
    duration: "5 MIN",
    desc: "Our 4-step process to ensure you never hire a scammer.",
    content: (
      <>
        <p><strong>Visual:</strong> BuildStart scans a builder's profile.</p>
        <p><strong>Narrator:</strong> "Why do we call it a Gauntlet? Because most directories let anyone in."</p>
        <p><strong>Step 1:</strong> "We check the Government Database. Is their license valid right now?"</p>
        <p><strong>Step 2:</strong> "Insurance. If they damage your home, are you covered?"</p>
        <div className="rh-expert-highlight"><strong>Expert Tip:</strong> Ask for a 'Certificate of Currency' for Public Liability insurance. If it's expired, they aren't insured.</div>
      </>
    )
  },
  {
    id: 3,
    title: "Council Approvals 101",
    duration: "4 MIN",
    desc: "DA vs CDC? Understanding legal requirements.",
    content: (
      <>
        <p><strong>Scene:</strong> Split screen. Left side: Slow turtle (DA). Right side: Fast hare (CDC).</p>
        <p><strong>Narrator:</strong> "DA (Development Application) goes through Council. It takes months. CDC (Complying Development) goes through a private certifier. It takes weeks."</p>
        <p><strong>Visual:</strong> Map of a property showing setbacks and height limits.</p>
        <div className="rh-expert-highlight"><strong>Expert Tip:</strong> If your design fits within 'Complying Development' rules, skip the Council drama and use a Private Certifier.</div>
      </>
    )
  },
  {
    id: 4,
    title: "The 'Prime Cost' Illusion",
    duration: "3 MIN",
    desc: "How 'allowances' in contracts trick you into signing.",
    content: (
      <>
        <p><strong>Scene:</strong> Contract close up: 'Tapware Allowance: $500'. Cut to showroom, couple looking at a $1,200 gold tap.</p>
        <p><strong>Narrator:</strong> "Your builder put in a 'Prime Cost' item. That is just a guess. If you pick the nice tap, you pay the extra $700 plus the builder's margin."</p>
        <div className="rh-expert-highlight"><strong>Expert Tip:</strong> Select your fixtures (tiles, taps, lights) BEFORE signing the contract so the price is fixed, not an estimate.</div>
      </>
    )
  },
  {
    id: 5,
    title: "The 'Low Bid' Trap",
    duration: "4 MIN",
    desc: "Why the cheapest quote is usually the most expensive.",
    content: (
      <>
        <p><strong>Visual:</strong> Three quotes on a table: $100k, $95k, and $65k.</p>
        <p><strong>Narrator:</strong> "It's tempting to take the $65k quote. But if a quote is 20% lower than the rest, they missed something. Or they plan to charge you via Variations later."</p>
        <div className="rh-expert-highlight"><strong>Expert Tip:</strong> Throw out the highest and lowest quote. The middle quote is usually the most honest assessment of the work.</div>
      </>
    )
  },
  {
    id: 6,
    title: "The Blue Tape Walkthrough",
    duration: "5 MIN",
    desc: "How to handle the final handover inspection.",
    content: (
      <>
        <p><strong>Scene:</strong> Empty, painted room. Homeowner walks in with a roll of blue painters tape.</p>
        <p><strong>Action:</strong> Sticking tape on a scratch in paint, a stiff door handle, and a gap in silicon.</p>
        <p><strong>Narrator:</strong> "This is Practical Completion. Do not pay the final invoice until you have 'Blue Taped' every defect."</p>
        <div className="rh-expert-highlight"><strong>Expert Tip:</strong> You have a legally protected defect period (usually 13 weeks). Use it.</div>
      </>
    )
  }
];

// --- DATA: PROJECT TEMPLATES ---
const projectTemplates = [
  { id: "100", title: "Cosmetic Kitchen Refresh", cost: "$20k - $60k", time: "3-6 Weeks", icon: "🍳", desc: "A facelift without structural changes. New cabinet fronts, benchtops, and appliances.", phases: ["Strip Out", "Rough-in", "Cabinet Install", "Splashback"], trades: ["Cabinet Maker", "Electrician", "Plumber", "Tiler"] },
  { id: "200", title: "Bathroom Renovation", cost: "$15k - $40k", time: "4-8 Weeks", icon: "🚿", desc: "Complete gut and replace. Requires waterproofing certification and precise tiling.", phases: ["Demo", "Waterproofing", "Tiling", "Fit-off"], trades: ["Plumber", "Waterproofer", "Tiler", "Electrician"] },
  { id: "300", title: "Knockdown Rebuild", cost: "$500k - $2M+", time: "12-24 Months", icon: "🏗️", desc: "Demolishing existing structure to build a custom modern home from scratch.", phases: ["DA Approval", "Slab Pour", "Frame", "Fit-out"], trades: ["Builder", "Excavator", "Concreter", "Certifier"] },
  { id: "108", title: "Full Interior Repaint", cost: "$10k - $40k", time: "2-4 Weeks", icon: <FaPaintRoller />, desc: "Repainting all internal walls, ceilings, doors, and trims to refresh the property.", phases: ["Sanding & Prep", "Masking", "Priming", "Top Coats"], trades: ["Painter", "Handyman"] },
  { id: "109", title: "New Flooring (Whole Home)", cost: "$15k - $50k", time: "2-3 Weeks", icon: <FaLayerGroup />, desc: "Replacing old carpet or tiles with new timber, vinyl, hybrid, or tile flooring.", phases: ["Remove Old Floor", "Level Slab", "Underlay", "Install"], trades: ["Flooring Installer", "Carpenter"] },
  { id: "300_Deck", title: "New Timber Deck", cost: "$15k - $60k", time: "2-4 Weeks", icon: <FaHammer />, desc: "Building a new timber or composite deck. Includes footings, sub-floor, and boards.", phases: ["Excavate Footings", "Sub-floor Frame", "Lay Decking", "Oiling"], trades: ["Carpenter", "Concreter", "Painter"] },
  { id: "305", title: "Landscaping Makeover", cost: "$20k - $100k+", time: "4-8 Weeks", icon: <FaTree />, desc: "Complete transformation of the garden. Earthworks, turf, planting, and retaining walls.", phases: ["Site Clearing", "Hardscaping", "Planting", "Turf"], trades: ["Landscaper", "Excavator", "Bricklayer"] },
  { id: "202", title: "Granny Flat Build", cost: "$120k - $250k", time: "3-6 Months", icon: <FaHome />, desc: "Constructing a self-contained secondary dwelling in the backyard.", phases: ["Approvals", "Slab", "Frame & Roof", "Fit-out"], trades: ["Builder", "Plumber", "Electrician", "Roofer"] },
  { id: "203", title: "Ground-Floor Extension", cost: "$100k - $400k", time: "4-8 Months", icon: "🏠", desc: "Extending the footprint of an existing home to add living space or bedrooms.", phases: ["Foundation", "Frame", "Roof Tie-in", "Internal Works"], trades: ["Builder", "Concreter", "Carpenter", "Roofer"] },
  { id: "303", title: "Swimming Pool", cost: "$50k - $120k+", time: "3-6 Months", icon: <FaSwimmingPool />, desc: "Excavation and installation of a concrete or fibreglass swimming pool.", phases: ["Excavation", "Shell Construction", "Filtration", "Fencing"], trades: ["Pool Builder", "Excavator", "Steel Fixer", "Tiler"] }
];

const ResourcesHubPage = () => {
  const navigate = useNavigate();
  const mountRef = useRef(null);
  const [activeTemplate, setActiveTemplate] = useState(projectTemplates[0]);
  const [activeVideo, setActiveVideo] = useState(videoScripts[0]);
  const [rotateAngle, setRotateAngle] = useState(0);
  
  // Carousel Logic (6 items = 60 degrees each)
  const rotateCarousel = (direction) => {
    // We update the state. The style={} prop in the DIV will pick this up.
    const angle = direction === 'next' ? rotateAngle - 60 : rotateAngle + 60;
    setRotateAngle(angle);
    console.log("Rotating to:", angle); // Debugging
  };

  return (
    <div className="bs-new-layout">
      <SocialProofEngine />
      <Header />

      {/* --- HERO SECTION --- */}
      <section className="rh-hero">
        <div className="rh-hero-overlay"></div>
        <div className="bs-container rh-hero-content">
          <div className="rh-tag">BUILDSTART ACADEMY</div>
          <h1 className="rh-title">
            MASTER THE ART OF <br/> 
            <span className="bs-text-gold-gradient">RENOVATION</span>
          </h1>
          <p className="rh-subtitle">
            Don't get ripped off. Understand the process, learn the terminology, 
            and build with the confidence of an industry expert.
          </p>
          <div className="rh-search-wrapper">
             <SearchFormComponent />
          </div>
        </div>
      </section>

      {/* --- 3D PROFESSION CAROUSEL --- */}
      <section className="rh-section rh-carousel-section">
        <div className="bs-container">
          <div className="rh-section-header">
            <h2>BUILD YOUR "A-TEAM"</h2>
            <p>Meet the essential players required for a successful build.</p>
          </div>
          
          <div className="rh-scene">
            <div 
              className="rh-carousel" 
              style={{ transform: `rotateY(${rotateAngle}deg)` }} // This is the engine of the animation
            >
              {professionals.map((prof, index) => (
                <div 
                  className="rh-carousel-cell" 
                  key={index} 
                  style={{ 
                    /* 6 Items = 60 degrees. translateZ(260px) pushes them out */
                    transform: `rotateY(${index * 60}deg) translateZ(260px)` 
                  }}
                >
                  <div className="rh-prof-img-placeholder" style={{backgroundColor: prof.color + '15'}}>
                     <div className="rh-prof-icon" style={{color: prof.color}}>{prof.icon}</div>
                  </div>
                  <div className="rh-card-inner">
                    <h3>{prof.name}</h3>
                    <div className="rh-prof-phase">
                        <span className="rh-phase-label">PHASE:</span>
                        <span className="rh-phase-value" style={{color: prof.color}}>{prof.phase}</span>
                    </div>
                    <p>{prof.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="rh-controls">
            <button className="rh-btn-prev" onClick={() => rotateCarousel('prev')}>
               &larr; Prev Trade
            </button>
            <button className="rh-btn-next" onClick={() => rotateCarousel('next')}>
               Next Trade &rarr;
            </button>
          </div>
        </div>
      </section>

      {/* --- NEW VIDEO SECTION --- */}
      <section className="rh-section rh-video-section">
        <div className="bs-container">
          <div className="rh-section-header">
             <h2>THE ACADEMY VAULT</h2>
             <p>Video tutorials to save you thousands in avoided mistakes.</p>
          </div>

          <div className="rh-video-container">
            {/* List Side */}
            <div className="rh-video-list">
              {videoScripts.map((video) => (
                <button 
                  key={video.id} 
                  className={`rh-video-btn ${activeVideo.id === video.id ? 'active' : ''}`}
                  onClick={() => setActiveVideo(video)}
                >
                  <div className="rh-video-btn-icon">
                    <FaPlayCircle />
                  </div>
                  <div className="rh-video-info">
                    <strong>{video.title}</strong>
                    <div className="rh-video-meta">
                      <FaClock size={12}/> {video.duration}
                    </div>
                  </div>
                  {activeVideo.id === video.id && <FaChevronRight className="rh-chevron"/>}
                </button>
              ))}
            </div>

            {/* Content Side */}
            <div className="rh-video-display">
               {/* Video Player Placeholder */}
               <div className="rh-player-box">
                  <div className="rh-play-overlay">
                    <FaPlay size={40} />
                  </div>
                  <div className="rh-player-text">VIDEO PREVIEW: {activeVideo.title}</div>
               </div>

               {/* Script Content */}
               <div className="rh-script-box">
                  <div className="rh-script-header">
                    <h4><FaClipboardCheck /> Video Transcript & Key Takeaways</h4>
                    <span>{activeVideo.duration} Read</span>
                  </div>
                  <div className="rh-script-body">
                    {activeVideo.content}
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>


      {/* ---  SCAM DETECTOR SECTION --- */}
      {/* 
         This section acts as a full-width break. 
         The widget handles its own dark background and spacing. 
      */}
      <section className="rh-scam-section-container">
         <ScamDetectorWidget />
      </section>



      {/* --- ESTIMATOR SECTION --- */}
      <section className="rh-section">
        <div className="bs-container">
          <div className="rh-section-header">
             <h2>WHAT DOES IT TAKE?</h2>
             <p>Select a project to see the AI-generated breakdown.</p>
          </div>

          <div className="rh-estimator-grid">
            <div className="rh-estimator-controls">
              {projectTemplates.map((temp) => (
                <button 
                  key={temp.id} 
                  className={`rh-temp-btn ${activeTemplate.id === temp.id ? 'active' : ''}`}
                  onClick={() => setActiveTemplate(temp)}
                >
                  <span className="rh-temp-icon">{temp.icon}</span>
                  <div className="rh-temp-info">
                    <strong>{temp.title}</strong>
                    <small>{temp.time}</small>
                  </div>
                  <FaChevronRight className="rh-chevron"/>
                </button>
              ))}
            </div>

            <div className="rh-blueprint-card">
              <div className="rh-blueprint-top">
                  <div className="rh-bp-title">{activeTemplate.title}</div>
                  <div className="rh-bp-cost">{activeTemplate.cost}</div>
              </div>
              <p className="rh-bp-desc">{activeTemplate.desc}</p>
              
              <div className="rh-bp-row">
                  <div className="rh-bp-col">
                      <h4>Key Phases</h4>
                      <ul>
                          {activeTemplate.phases.map((p, i) => <li key={i}><FaCheck size={10}/> {p}</li>)}
                      </ul>
                  </div>
                  <div className="rh-bp-col">
                      <h4>Required Pros</h4>
                      <div className="rh-tags">
                          {activeTemplate.trades.map((t, i) => <span key={i}>{t}</span>)}
                      </div>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      <AppDownloadSection />

      <TestimonialsSection />


      <FinalCtaSection 
        title="Ready to start your project " 
        highlighttext="the right way?" 
        subtitle="Join thousands of homeowners who built with confidence using BuildStart." 
      />


      {/* --- FINAL CTA SECTION --- */}
      {/*
      <section className="rh-cta-section">
         <div className="bs-container rh-cta-content">
            <h2>Ready to start your project the right way?</h2>
            <p>Join thousands of homeowners who built with confidence using BuildStart.</p>
            <button className="rh-cta-btn" onClick={() => navigate('/role-select')}>
               Get Started for Free <FaArrowRight />
            </button>
         </div>
      </section>
      */}


      <Footer />
    </div>
  );
};

export default ResourcesHubPage;